import React, { useRef, useMemo, useEffect, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Polygon,
  Circle,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ReactComponent as Locator } from "../../CheckoutProcess/SecondStep/location.svg";
import { ReactComponent as LocatorPin } from "./locator.svg";
import MapSearchInput from "../../CheckoutProcess/SecondStep/MapSearchInput";
import instance from "../../../store/actions/instance";

/** ---------- Geometry helpers ---------- */
const isPointInPolygon = (point, polygon) => {
  let inside = false;
  const x = point.lat,
    y = point.lng;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lat,
      yi = polygon[i].lng;
    const xj = polygon[j].lat,
      yj = polygon[j].lng;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

const isPointInCircle = (point, center, radius) => {
  return L.latLng(point).distanceTo(L.latLng(center)) <= radius;
};

const findContainingArea = (point, areas) => {
  for (const area of areas) {
    if (!area.shapes?.length) continue;

    for (const shape of area.shapes) {
      const inside =
        (shape.type === "Polygon" && isPointInPolygon(point, shape.points)) ||
        (shape.type === "Circle" &&
          isPointInCircle(point, shape.center, shape.radius));

      if (inside) {
        return {
          inside: true,
          branchDeliveryChargeId: area.id,
          country_id: area.country_id,
        };
      }
    }
  }

  return { inside: false, branchDeliveryChargeId: null, country_id: null };
};

const getBoundsFromShapes = (areas) => {
  const latlngs = [];
  areas.forEach((area) => {
    if (!area.shapes?.length) return;
    area.shapes.forEach((shape) => {
      if (shape.type === "Polygon") {
        shape.points.forEach((p) => latlngs.push([p.lat, p.lng]));
      }
      if (shape.type === "Circle") {
        latlngs.push([
          shape.center.lat + shape.radius / 111320,
          shape.center.lng +
            shape.radius /
              (111320 * Math.cos((shape.center.lat * Math.PI) / 180)),
        ]);
        latlngs.push([
          shape.center.lat - shape.radius / 111320,
          shape.center.lng -
            shape.radius /
              (111320 * Math.cos((shape.center.lat * Math.PI) / 180)),
        ]);
      }
    });
  });

  return L.latLngBounds(latlngs);
};

/** ---------- Map Move Handler ---------- */
const MapMoveHandler = ({ onMapMoveEnd }) => {
  useMapEvents({
    moveend: (e) => {
      const map = e.target;
      const center = map.getCenter();
      onMapMoveEnd({ lat: center.lat, lng: center.lng });
    },
  });

  return null;
};

const SelectedCountryRepositioner = ({
  selectedCountryID,
  countryAreas,
  lat,
  lng,
  onProgrammaticMove,
}) => {
  const map = useMapEvents({});
  const prevSelectedCountryID = useRef(undefined);

  const bounds = useMemo(
    () => getBoundsFromShapes(countryAreas),
    [countryAreas]
  );

  useEffect(() => {
    if (!map) return;

    if (prevSelectedCountryID.current === selectedCountryID) return;
    prevSelectedCountryID.current = selectedCountryID;

    if (selectedCountryID == null) return;
    if (!countryAreas?.length || !bounds.isValid()) return;

    // If saved lat/lng exists and is inside that country's shapes, center there (keep zoom)
    if (lat && lng) {
      const savedPoint = { lat, lng };
      const insideCountry = findContainingArea(savedPoint, countryAreas).inside;
      if (insideCountry) {
        if (map.stop) map.stop();
        onProgrammaticMove();
        map.setView([lat, lng], map.getZoom(), { animate: true });
        return;
      }
    }

    // Fit bounds to selected country, but don't zoom in more than current zoom (and cap)
    const currentZoom = map.getZoom();
    if (map.stop) map.stop();
    onProgrammaticMove();
    map.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: Math.min(currentZoom, 14),
      animate: true,
    });
  }, [
    map,
    selectedCountryID,
    countryAreas,
    bounds,
    lat,
    lng,
    onProgrammaticMove,
  ]);

  return null;
};

const DeliveryAreasMap = ({
  branchDeliveryCharges,
  selectedCountryID,
  onPinSelected,
  lat,
  lng,
  isInsidePolygon,
  height = 500,
  language,
  countryCodes = ["kw", "eg", "sa", "bh", "om", "qa"],
  setLoading,
  settings,
}) => {
  const mapRef = useRef(null);

  // latest-wins reverse geocode
  const geocodeRequestIdRef = useRef(0);

  // skip programmatic moveend events (counter > 0)
  const skipMoveEndCountRef = useRef(0);

  // avoid duplicate geocode calls while an identical request is in-flight
  const inflightKeyRef = useRef(null);

  const allAreas = useMemo(
    () => (branchDeliveryCharges || []).filter((a) => a.shapes?.length),
    [branchDeliveryCharges]
  );

  const countryAreas = useMemo(() => {
    if (selectedCountryID == null) return [];
    return allAreas.filter((a) => a.country_id === selectedCountryID);
  }, [allAreas, selectedCountryID]);

  const initialBounds = useMemo(
    () => getBoundsFromShapes(allAreas),
    [allAreas]
  );

  const onProgrammaticMove = useCallback(() => {
    skipMoveEndCountRef.current += 1;
  }, []);

  const reverseGeocode = useCallback(
    async (lat2, lng2) => {
      if (!settings?.enable_reverse_geocode) return null;

      try {
        if (setLoading) setLoading(true);
        const res = await instance.post("/map_reverse_geo/", {
          lat: lat2,
          lng: lng2,
          language: language === "arabic" ? "ar" : "en",
        });
        return res.data;
      } finally {
        if (setLoading) setLoading(false);
      }
    },
    [language, settings?.enable_reverse_geocode, setLoading]
  );

  /**
   * ✅ Single source of truth:
   * - move/search/locate -> selectPoint
   * - selectPoint calls onPinSelected ONLY ONCE
   * - when outside polygon: DO NOT emit country_id=null (fallback to selectedCountryID)
   */
  const selectPoint = useCallback(
    async ({ lat: lat2, lng: lng2 }, opts = {}) => {
      const { flyTo = false, zoom } = opts;
      const map = mapRef.current;

      if (flyTo && map) {
        if (map.stop) map.stop();
        onProgrammaticMove();
        map.flyTo([lat2, lng2], zoom ?? Math.max(map.getZoom(), 15), {
          animate: true,
          duration: 0.8,
        });
      }

      const containment = findContainingArea(
        { lat: lat2, lng: lng2 },
        allAreas
      );

      if (isInsidePolygon) isInsidePolygon(containment.inside);

      // ✅ prevent parent from flipping selectedCountryID to null
      const stableCountryId =
        containment.country_id != null
          ? containment.country_id
          : selectedCountryID;

      // ✅ in-flight de-dupe (round to avoid float noise)
      const key = `${Number(lat2).toFixed(6)},${Number(lng2).toFixed(6)}`;
      if (inflightKeyRef.current === key) {
        // already geocoding this point → just emit minimal info once
        onPinSelected({
          lat: lat2,
          lng: lng2,
          branchDeliveryChargeId: containment.branchDeliveryChargeId,
          country_id: stableCountryId,
          is_inside: containment.inside,
        });
        return;
      }
      inflightKeyRef.current = key;

      const requestId = ++geocodeRequestIdRef.current;

      let geo = null;
      if (containment.inside) {
        try {
          geo = await reverseGeocode(lat2, lng2);
        } catch (e) {
          // ignore
        }
      }
      // clear inflight key only if this request is the latest request
      if (requestId === geocodeRequestIdRef.current) {
        inflightKeyRef.current = null;
      }

      // latest-wins guard
      if (requestId !== geocodeRequestIdRef.current) return;

      const addr = geo?.address || {};

      onPinSelected({
        lat: lat2,
        lng: lng2,
        branchDeliveryChargeId: containment.branchDeliveryChargeId,
        country_id: stableCountryId,

        // reverse geocode fields (best-effort)
        street: addr.road || addr.amenity || addr.shop || addr.tourism || "",
        building: addr.house_number || "",
        block: addr.neighbourhood || "",
        display_name: geo?.display_name || "",
        geocode_provider: geo ? "nominatim" : "",
        geocode_confidence: addr.house_number
          ? "exact"
          : geo
          ? "approximate"
          : "",
        is_inside: containment.inside,
      });
    },
    [
      allAreas,
      isInsidePolygon,
      onPinSelected,
      reverseGeocode,
      onProgrammaticMove,
      selectedCountryID,
    ]
  );

  const handleSearchSelect = useCallback(
    ({ lat: lat2, lng: lng2 }) => {
      selectPoint({ lat: lat2, lng: lng2 }, { flyTo: true, zoom: 17 });
    },
    [selectPoint]
  );

  const goToUserLocation = useCallback(() => {
    const map = mapRef.current;
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };

        if (map) {
          if (map.stop) map.stop();
          onProgrammaticMove();
          map.flyTo([loc.lat, loc.lng], Math.max(map.getZoom(), 15), {
            animate: true,
            duration: 0.8,
          });
        }

        selectPoint(loc, { flyTo: false });
      },
      (err) => console.error(err),
      { enableHighAccuracy: true }
    );
  }, [selectPoint, onProgrammaticMove]);

  const initialCenter = [0, 0];
  const initialZoom = 2;

  return (
    <div style={{ position: "relative", height }}>
      {settings?.enable_map_search && (
        <MapSearchInput
          language={language}
          countryCodes={countryCodes}
          onSelect={handleSearchSelect}
        />
      )}

      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ height: "100%", width: "100%", zIndex: 0 }}
        scrollWheelZoom
        zoomSnap={0.5}
        zoomDelta={0.5}
        whenCreated={(map) => {
          mapRef.current = map;

          // 1) Saved lat/lng
          if (lat && lng) {
            onProgrammaticMove();
            map.setView([lat, lng], 15);
            selectPoint({ lat, lng }, { flyTo: false });
            return;
          }

          // 2) Try geolocation
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const loc = {
                  lat: pos.coords.latitude,
                  lng: pos.coords.longitude,
                };
                onProgrammaticMove();
                map.setView([loc.lat, loc.lng], 15);
                selectPoint(loc, { flyTo: false });
              },
              () => {
                // 3) fallback: fit to ALL polygons initially
                if (initialBounds.isValid()) {
                  onProgrammaticMove();
                  map.fitBounds(initialBounds, {
                    padding: [50, 50],
                    maxZoom: 14,
                  });
                }
              },
              { enableHighAccuracy: true }
            );
            return;
          }

          // 3) fallback: fit to ALL polygons initially
          if (initialBounds.isValid()) {
            onProgrammaticMove();
            map.fitBounds(initialBounds, { padding: [50, 50], maxZoom: 14 });
          }
        }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />

        {/* Render ALL polygons/circles (invisible, but available for hit-testing) */}
        {allAreas.map((area) =>
          area.shapes.map((shape, idx) => {
            if (shape.type === "Polygon")
              return (
                <Polygon
                  key={`${area.id}-poly-${idx}`}
                  positions={shape.points.map((p) => [p.lat, p.lng])}
                  pathOptions={{
                    color: "#1976d200",
                    opacity: 0,
                    weight: 2,
                    fillOpacity: 0,
                  }}
                />
              );
            if (shape.type === "Circle")
              return (
                <Circle
                  key={`${area.id}-circle-${idx}`}
                  center={[shape.center.lat, shape.center.lng]}
                  radius={shape.radius}
                  pathOptions={{
                    color: "#388e3c00",
                    opacity: 0,
                    weight: 2,
                    fillOpacity: 0,
                  }}
                />
              );
            return null;
          })
        )}

        {/* Reposition ONLY when selectedCountryID changes */}
        <SelectedCountryRepositioner
          selectedCountryID={selectedCountryID}
          countryAreas={countryAreas}
          lat={lat}
          lng={lng}
          onProgrammaticMove={onProgrammaticMove}
        />

        {/* Move end → selection (skip programmatic moveends) */}
        <MapMoveHandler
          onMapMoveEnd={({ lat: lat2, lng: lng2 }) => {
            if (skipMoveEndCountRef.current > 0) {
              skipMoveEndCountRef.current -= 1;
              return;
            }
            selectPoint({ lat: lat2, lng: lng2 }, { flyTo: false });
          }}
        />
      </MapContainer>

      {/* Center pin */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-100%)",
          width: 24,
          height: 36,
          pointerEvents: "none",
          zIndex: 500,
        }}
      >
        <LocatorPin
          style={{
            width: 40,
            height: 40,
            position: "absolute",
            inset: 0,
            fill: "#606060",
            margin: "auto",
          }}
        />
      </div>

      {/* Locator button */}
      <div
        role="button"
        tabIndex={0}
        style={{
          position: "absolute",
          right: 10,
          top: 10,
          width: 50,
          height: 50,
          background: "white",
          borderRadius: 4,
          cursor: "pointer",
          zIndex: 9999,
          border: "2px solid rgba(0, 0, 0, 0.2)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: "auto",
        }}
        onClick={goToUserLocation}
        onMouseDown={(e) => {
          e.preventDefault();
          goToUserLocation();
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          goToUserLocation();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") goToUserLocation();
        }}
      >
        <Locator style={{ width: 25, height: 25, fill: "#606060" }} />
      </div>
    </div>
  );
};

export default DeliveryAreasMap;
